import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { REACTION_TYPES } from './ReactionPicker';
import { cn } from '@/lib/utils';
import { Trophy, Flame, Star, Sparkles } from 'lucide-react';

interface DraftPickCardProps {
  pickNumber: number;
  post: {
    id: string;
    content: string | null;
    media_urls: string[] | null;
    reactions_count: Record<string, number>;
    created_at: string;
    profiles: {
      id: string;
      name: string | null;
      avatar_url: string | null;
      username: string | null;
    };
  };
  totalReactions: number;
  reactionVariety: number;
}

const getPickStyle = (pickNumber: number) => {
  if (pickNumber === 1) {
    return {
      border: 'border-yellow-500/50',
      bg: 'bg-gradient-to-br from-yellow-500/20 via-amber-500/10 to-orange-500/20',
      glow: 'shadow-lg shadow-yellow-500/30',
      badge: 'bg-gradient-to-r from-yellow-400 to-amber-500 text-black',
      icon: Trophy,
    };
  }
  if (pickNumber <= 5) {
    return {
      border: 'border-orange-500/40',
      bg: 'bg-gradient-to-br from-orange-500/15 to-red-500/10',
      glow: 'shadow-md shadow-orange-500/20',
      badge: 'bg-gradient-to-r from-orange-400 to-red-500 text-white',
      icon: Flame,
    };
  }
  if (pickNumber <= 10) {
    return {
      border: 'border-blue-500/30',
      bg: 'bg-gradient-to-br from-blue-500/10 to-indigo-500/5',
      glow: '',
      badge: 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white',
      icon: Star,
    };
  }
  return {
    border: 'border-border',
    bg: 'bg-card',
    glow: '',
    badge: 'bg-muted text-muted-foreground',
    icon: Sparkles,
  };
};

export const DraftPickCard: React.FC<DraftPickCardProps> = ({
  pickNumber,
  post,
  totalReactions,
  reactionVariety,
}) => {
  const style = getPickStyle(pickNumber);
  const IconComponent = style.icon;

  // Get top reactions
  const topReactions = Object.entries(post.reactions_count || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 4);

  const getReactionEmoji = (type: string) => {
    return REACTION_TYPES.find(r => r.id === type)?.emoji || '❓';
  };

  return (
    <div
      className={cn(
        'relative rounded-xl border p-4 transition-all hover:scale-[1.02]',
        style.border,
        style.bg,
        style.glow,
        pickNumber === 1 && 'animate-pulse'
      )}
    >
      {/* Pick Number Badge */}
      <div
        className={cn(
          'absolute -top-3 -left-2 flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold',
          style.badge
        )}
      >
        <IconComponent className="h-4 w-4" />
        <span>#{pickNumber}</span>
      </div>

      {/* Author Info */}
      <div className="flex items-center gap-3 mt-2 mb-3">
        <Avatar className="h-10 w-10 border-2 border-background">
          <AvatarImage src={post.profiles?.avatar_url || undefined} />
          <AvatarFallback className="bg-primary/20 text-primary font-bold">
            {post.profiles?.name?.charAt(0) || '?'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">
            {post.profiles?.name || 'Unknown'}
          </p>
          {post.profiles?.username && (
            <p className="text-xs text-muted-foreground">@{post.profiles.username}</p>
          )}
        </div>
      </div>

      {/* Post Preview */}
      {post.media_urls?.[0] && (
        <div className="mb-3 rounded-lg overflow-hidden aspect-video bg-muted">
          <img
            src={post.media_urls[0]}
            alt="Post media"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {post.content && (
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {post.content}
        </p>
      )}

      {/* Reactions Summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {topReactions.map(([type, count]) => (
            <div
              key={type}
              className="flex items-center gap-0.5 bg-muted/50 rounded-full px-2 py-0.5 text-xs"
            >
              <span>{getReactionEmoji(type)}</span>
              <span className="font-medium">{count}</span>
            </div>
          ))}
        </div>
        <div className="text-xs text-muted-foreground">
          {totalReactions} reactions • {reactionVariety} types
        </div>
      </div>
    </div>
  );
};
